import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { ExportButton } from '../../components/ExportButton';
import { toast } from 'sonner';
import { Clock, Search, Filter, ChevronDown, ChevronUp, ShoppingBag, User, Phone, XCircle, AlertTriangle, RefreshCw, Calendar, CalendarDays, CheckSquare, Square, ArrowUpDown } from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/errorHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  'Reçue': 'bg-orange-100 text-orange-700 border-orange-200',
  'Planifiée': 'bg-purple-100 text-purple-700 border-purple-200',
  'En préparation': 'bg-blue-100 text-blue-700 border-blue-200',
  'Prête': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Terminée': 'bg-gray-100 text-gray-600 border-gray-200',
  'Annulée': 'bg-red-100 text-red-700 border-red-200',
  'pending_payment': 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const statusLabel = (status) => status === 'pending_payment' ? 'Paiement en cours' : status;

const nextStatus = {
  'Reçue': 'En préparation',
  'Planifiée': 'En préparation',
  'En préparation': 'Prête',
  'Prête': 'Terminée',
  'pending_payment': 'Reçue',
};

// Auto-refresh interval in milliseconds (10 seconds)
const AUTO_REFRESH_INTERVAL = 10000;

export default function RestaurantOrders() {
  useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [updating, setUpdating] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [expandedOrders, setExpandedOrders] = useState({});
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const previousOrderCount = useRef(0);
  const [cancelling, setCancelling] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [dateFilter, setDateFilter] = useState(''); // YYYY-MM-DD for exact day filter

  const fetchOrders = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    
    try {
      const url = `${API}/restaurant-dashboard/orders`;
      const response = await axios.get(url);
      
      const newOrders = response.data;
      
      // Check for new orders (only "Reçue" status)
      const newPendingOrders = newOrders.filter(o => o.status === 'Reçue');
      const previousPending = previousOrderCount.current;
      
      if (previousPending > 0 && newPendingOrders.length > previousPending) {
        // New order received!
        setNewOrderAlert(true);
        toast.success('Nouvelle commande reçue!', {
          duration: 5000,
        });
        // Play notification sound if available
        try {
          const audio = new Audio('/notification.mp3');
          audio.play().catch(() => { /* Autoplay blocked */ });
        } catch { /* Sound not available */ }
      }
      
      previousOrderCount.current = newPendingOrders.length;
      setOrders(newOrders);
      setLastUpdate(new Date());
      setNewOrderAlert(false);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchOrders(true);
  };

  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 10 seconds for real-time updates
    const interval = setInterval(() => fetchOrders(false), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleUpdateStatus = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await axios.put(`${API}/restaurant-dashboard/orders/${orderId}/status?status=${encodeURIComponent(newStatus)}`, {});
      toast.success(`Statut mis à jour: ${newStatus}`);
      fetchOrders();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(null);
    }
  };

  const openCancelModal = (order) => {
    setOrderToCancel(order);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancelOrder = async () => {
    if (!orderToCancel) return;
    if (!cancelReason.trim()) {
      toast.error('Veuillez indiquer une raison d\'annulation');
      return;
    }
    
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API}/payments/refund/${orderToCancel.id}?reason=${encodeURIComponent(cancelReason)}`,
        {}
      );
      
      if (response.data.refund?.refund_id) {
        toast.success('Commande annulée et client remboursé');
      } else {
        toast.success('Commande annulée');
      }
      
      setShowCancelModal(false);
      setOrderToCancel(null);
      fetchOrders();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'annulation"));
    } finally {
      setCancelling(false);
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const toggleSelectOrder = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedOrders.length === 0) return;
    setBulkUpdating(true);
    try {
      const res = await axios.put(`${API}/restaurant-dashboard/orders/bulk-status`, {
        order_ids: selectedOrders,
        status: newStatus
      });
      toast.success(`${res.data.updated} commande(s) mise(s) a jour`);
      setSelectedOrders([]);
      fetchOrders();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erreur lors de la mise a jour en lot"));
    } finally {
      setBulkUpdating(false);
    }
  };

  const [resyncing, setResyncing] = useState(false);
  const [piRecoverOpen, setPiRecoverOpen] = useState(false);
  const [piInput, setPiInput] = useState('');
  const [piRecovering, setPiRecovering] = useState(false);
  const [piRecoverResult, setPiRecoverResult] = useState(null);

  const handleRecoverFromPi = async () => {
    if (piRecovering || !piInput.trim()) return;
    setPiRecovering(true);
    setPiRecoverResult(null);
    try {
      const res = await axios.post(`${API}/restaurant-dashboard/orders/recover-from-stripe`, {
        payment_intent_id: piInput.trim()
      });
      setPiRecoverResult(res.data);
      if (res.data?.action === 'restored') {
        toast.success(`Commande ${res.data.order_number || ''} recuperee !`);
        fetchOrders();
      } else if (!res.data?.found_in_stripe) {
        toast.error('Payment Intent non trouve sur Stripe');
      } else if (!res.data?.found_in_db) {
        toast.warning('Commande introuvable en base. Voir details ci-dessous.');
      } else {
        toast.info(`Statut actuel: ${res.data.current_status}`);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la recuperation'));
    } finally {
      setPiRecovering(false);
    }
  };

  const handleResyncStripe = async () => {
    if (resyncing) return;
    setResyncing(true);
    try {
      const res = await axios.post(`${API}/restaurant-dashboard/orders/resync-stripe`);
      const { restored, still_pending, total_scanned } = res.data || {};
      if (restored > 0) {
        toast.success(`${restored} commande(s) recuperee(s) ! (${total_scanned} verifiee(s))`);
      } else if (still_pending > 0) {
        toast.info(`Aucune commande a recuperer. ${still_pending} en attente de paiement.`);
      } else {
        toast.info('Toutes les commandes sont a jour');
      }
      fetchOrders();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la resynchronisation'));
    } finally {
      setResyncing(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    // Status filter
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const matchesSearch = (
        order.order_number?.toLowerCase().includes(search) ||
        order.client?.name?.toLowerCase().includes(search) ||
        order.guest_name?.toLowerCase().includes(search) ||
        order.client_name?.toLowerCase().includes(search)
      );
      if (!matchesSearch) return false;
    }
    
    // Type filter (today vs reservations)
    if (typeFilter === 'today') {
      if (order.is_reservation) return false;
    } else if (typeFilter === 'reservations') {
      if (!order.is_reservation) return false;
    }
    
    // Date filter
    if (dateFilter) {
      const orderDate = (order.created_at || '').substring(0, 10);
      if (orderDate !== dateFilter) return false;
    }
    
    return true;
  }).sort((a, b) => {
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });

  // Helper to format pickup date for display
  const formatPickupDate = (order) => {
    if (!order.pickup_date) return null;
    const today = new Date().toISOString().split('T')[0];
    if (order.pickup_date === today) return null;
    
    return new Date(order.pickup_date).toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    
    // If it's already a simple time format like "13:00", return it directly
    if (/^\d{2}:\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Otherwise parse as ISO datetime
    try {
      return new Date(dateString).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout title="Historique des commandes">
      {/* Live Indicator with Refresh Button */}
      <div className="flex items-center justify-between gap-2 mb-4 py-2 px-4 bg-emerald-50 rounded-xl">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="text-sm font-medium text-emerald-700">En direct</span>
          <span className="text-xs text-emerald-600">
            • Actualisation auto toutes les 10s
          </span>
          <span className="text-xs text-emerald-600">
            • Mis à jour {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleManualRefresh}
          disabled={refreshing}
          className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleResyncStripe}
          disabled={resyncing}
          className="text-indigo-700 border-indigo-300 hover:bg-indigo-50"
          data-testid="resync-stripe-btn"
          title="Recupere les commandes payees sur Stripe qui n'apparaissent pas"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${resyncing ? 'animate-spin' : ''}`} />
          {resyncing ? 'Sync...' : 'Resync Stripe'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPiRecoverOpen(v => !v)}
          className="text-purple-700 border-purple-300 hover:bg-purple-50"
          data-testid="recover-pi-btn"
        >
          Recuperer via Payment Intent
        </Button>
        <ExportButton />
      </div>

      {/* Payment Intent Recovery Panel */}
      {piRecoverOpen && (
        <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-xl" data-testid="recover-pi-panel">
          <h3 className="font-semibold text-purple-900 mb-2">Recuperer une commande depuis Stripe</h3>
          <p className="text-sm text-purple-700 mb-3">
            Collez le Payment Intent ID (commence par <code>pi_</code>) trouve dans votre dashboard Stripe.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Input
              value={piInput}
              onChange={(e) => setPiInput(e.target.value)}
              placeholder="pi_3TjyqmDMtPFi62Hs0qj..."
              className="flex-1 min-w-[260px]"
              data-testid="recover-pi-input"
            />
            <Button
              onClick={handleRecoverFromPi}
              disabled={piRecovering || !piInput.trim()}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="recover-pi-submit"
            >
              {piRecovering ? 'Recherche...' : 'Recuperer'}
            </Button>
          </div>
          {piRecoverResult && (
            <div className="mt-3 p-3 bg-white rounded-lg border border-purple-200 text-sm space-y-1" data-testid="recover-pi-result">
              <div>Stripe : {piRecoverResult.found_in_stripe ? '✓ trouve' : '✗ non trouve'}</div>
              {piRecoverResult.found_in_stripe && (
                <>
                  <div>Montant : {piRecoverResult.amount_total} €</div>
                  <div>Statut paiement : {piRecoverResult.payment_status}</div>
                  <div>Email client : {piRecoverResult.customer_email || 'N/A'}</div>
                  <div>En base : {piRecoverResult.found_in_db ? `✓ ${piRecoverResult.order_number || ''} (statut: ${piRecoverResult.current_status})` : '✗ introuvable'}</div>
                  {piRecoverResult.action === 'restored' && (
                    <div className="text-emerald-700 font-semibold">→ Restauree : {piRecoverResult.new_status}</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Order Alert Banner */}
      {newOrderAlert && (
        <div className="mb-4 p-3 bg-orange-100 border border-orange-300 rounded-xl flex items-center gap-3 animate-pulse">
          <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
            <ShoppingBag className="w-4 h-4 text-white" />
          </div>
          <span className="font-medium text-orange-800">🔔 Nouvelle commande reçue!</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher par n° ou client..."
            className="pl-12 rounded-xl"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-48 rounded-xl">
            <CalendarDays className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Tous les types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="today">Aujourd'hui</SelectItem>
            <SelectItem value="reservations">Réservations</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 rounded-xl">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Tous les statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="Reçue">Reçue</SelectItem>
            <SelectItem value="Planifiée">Planifiée</SelectItem>
            <SelectItem value="En préparation">En préparation</SelectItem>
            <SelectItem value="Prête">Prête</SelectItem>
            <SelectItem value="Terminée">Terminée</SelectItem>
            <SelectItem value="Annulée">Annulée</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {/* Bulk actions bar + sort */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <button onClick={toggleSelectAll} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900" data-testid="select-all-orders">
              {selectedOrders.length === filteredOrders.length && filteredOrders.length > 0
                ? <CheckSquare className="w-4 h-4 text-emerald-500" />
                : <Square className="w-4 h-4" />
              }
              {selectedOrders.length > 0 ? `${selectedOrders.length} selectionnee(s)` : 'Tout selectionner'}
            </button>
            {selectedOrders.length > 0 && (
              <div className="flex gap-1 ml-2">
                {['En préparation', 'Prête', 'Terminée'].map(s => (
                  <Button key={s} size="sm" variant="outline" disabled={bulkUpdating} onClick={() => handleBulkStatusChange(s)} className="text-xs h-7" data-testid={`bulk-${s}`}>
                    {bulkUpdating ? '...' : s}
                  </Button>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-2 py-1 text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
              data-testid="date-filter"
            />
            {dateFilter && (
              <button onClick={() => setDateFilter('')} className="text-xs text-gray-400 hover:text-gray-600" data-testid="clear-date-filter">
                Effacer
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500 bg-white rounded-2xl border">Chargement...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500 bg-white rounded-2xl border">Aucune commande trouvee</div>
        ) : (
          filteredOrders.map((order) => (
            <div key={order.id} className={`bg-white rounded-2xl border overflow-hidden mb-3 ${selectedOrders.includes(order.id) ? 'border-emerald-400 ring-1 ring-emerald-200' : 'border-gray-200'}`}>
              {/* Order Header - Always Visible */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-3"
              >
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSelectOrder(order.id); }}
                  className="shrink-0"
                  data-testid={`select-order-${order.id}`}
                >
                  {selectedOrders.includes(order.id)
                    ? <CheckSquare className="w-5 h-5 text-emerald-500" />
                    : <Square className="w-5 h-5 text-gray-300" />
                  }
                </button>
                <div className="flex-1 min-w-0" onClick={() => toggleExpand(order.id)}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Left: Order info */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-gray-900">{order.order_number}</span>
                        <Badge className={statusColors[order.status]}>
                          {statusLabel(order.status)}
                        </Badge>
                        {order.highlight_tags && order.highlight_tags.map(tag => (
                          <Badge key={tag} className="bg-violet-100 text-violet-700 border-violet-200" data-testid="highlight-tag">
                            {tag}
                          </Badge>
                        ))}
                        {order.is_reservation && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                            <Calendar className="w-3 h-3 mr-1" />
                            Réservation
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          {order.guest_name || order.client_name || order.client?.name || 'Client'}
                        </span>
                        <span>
                          Passée le {formatDate(order.created_at)} à {formatTime(order.created_at)}
                        </span>
                      </div>
                      {/* Aperçu des articles */}
                      {order.items && order.items.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1.5">
                          {order.items.map(i => `${i.quantity}x ${i.dish_name}`).join(', ')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right: Times & Amount */}
                  <div className="flex items-center gap-4 md:gap-6">
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Retrait</p>
                      {formatPickupDate(order) ? (
                        <div>
                          <p className="font-medium text-purple-600 text-xs">
                            {formatPickupDate(order)}
                          </p>
                          <p className="font-semibold text-gray-900 flex items-center gap-1">
                            <Clock className="w-4 h-4 text-emerald-500" />
                            {formatTime(order.pickup_slot)}
                          </p>
                        </div>
                      ) : (
                        <p className="font-semibold text-gray-900 flex items-center gap-1">
                          <Clock className="w-4 h-4 text-emerald-500" />
                          {formatTime(order.pickup_slot)}
                        </p>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-gray-500">Montant</p>
                      <p className="font-bold text-emerald-600">{order.total_amount?.toFixed(2)} €</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {nextStatus[order.status] && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateStatus(order.id, nextStatus[order.status]);
                          }}
                          disabled={updating === order.id}
                          className="rounded-full text-xs whitespace-nowrap"
                        >
                          {updating === order.id ? '...' : nextStatus[order.status]}
                        </Button>
                      )}
                      {/* Cancel button - only for pending/preparing orders */}
                      {['Reçue', 'En préparation'].includes(order.status) && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCancelModal(order);
                          }}
                          className="rounded-full text-xs whitespace-nowrap text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                          Annuler
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(order.id);
                        }}
                      >
                        {expandedOrders[order.id] ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                </div>{/* close flex-1 wrapper */}
              </div>

              {/* Order Details - Expandable */}
              {expandedOrders[order.id] && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Items to prepare */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <ShoppingBag className="w-4 h-4 text-emerald-500" />
                        Articles à préparer ({order.items?.length || 0})
                      </h4>
                      <div className="space-y-3">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="bg-white rounded-xl p-3 border border-gray-200">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">
                                    {item.quantity}
                                  </span>
                                  <span className="font-semibold text-gray-900">{item.dish_name}</span>
                                </div>
                                
                                {/* Selected options (text_input, choices) */}
                                {item.selected_option_names && item.selected_option_names.length > 0 && (
                                  <div className="mt-2 pl-8" data-testid={`order-item-options-${idx}`}>
                                    <span className="text-sm text-indigo-700 font-medium">
                                      ⚙ {item.selected_option_names.join(' · ')}
                                    </span>
                                  </div>
                                )}

                                {/* Selected ingredients grouped by category */}
                                {item.ingredient_details && item.ingredient_details.length > 0 && (
                                  <div className="mt-2 pl-8 space-y-1">
                                    {item.ingredient_details.map((group, gIdx) => (
                                      <div key={gIdx} className="text-sm">
                                        <span className="font-medium text-gray-700">{group.category} :</span>{' '}
                                        <span className="text-emerald-600">{group.items.join(', ')}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Fallback: flat ingredient names (legacy) */}
                                {!item.ingredient_details && item.selected_ingredient_names && item.selected_ingredient_names.length > 0 && (
                                  <div className="mt-2 pl-8">
                                    <span className="text-sm text-emerald-600">
                                      {item.selected_ingredient_names.join(', ')}
                                    </span>
                                  </div>
                                )}

                                {/* Legacy ingredients format */}
                                {!item.ingredient_details && !item.selected_ingredient_names && item.ingredients && item.ingredients.length > 0 && (
                                  <p className="text-sm text-gray-500 mt-1 pl-8">
                                    {item.ingredients.join(', ')}
                                  </p>
                                )}

                                {/* Special instructions */}
                                {item.special_instructions && (
                                  <p className="text-sm text-amber-600 mt-2 pl-8 italic">
                                    📝 {item.special_instructions}
                                  </p>
                                )}
                              </div>
                              <span className="font-semibold text-gray-900 ml-4">
                                {item.total_price?.toFixed(2)} €
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Client info & Notes */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-500" />
                        Informations client
                      </h4>
                      <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900" data-testid="order-client-name">
                              {order.guest_name 
                                ? order.guest_name
                                : order.client?.first_name && order.client?.last_name 
                                  ? `${order.client.first_name} ${order.client.last_name}`
                                  : order.client_name || order.client?.name || 'Client'}
                            </p>
                            {order.guest_name && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Invité</span>
                            )}
                            <p className="text-sm text-gray-500">
                              {order.guest_email || order.client?.email}
                            </p>
                          </div>
                        </div>
                        {(order.guest_phone || order.client?.phone) && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4" />
                            {order.guest_phone || order.client.phone}
                          </div>
                        )}
                      </div>

                      {/* Client notes/remarks */}
                      {order.client_notes && (
                        <div className="mt-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Remarques du client</h4>
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                            📝 {order.client_notes}
                          </div>
                        </div>
                      )}

                      {/* Special instructions from checkout */}
                      {order.special_instructions && (
                        <div className="mt-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Instructions spéciales</h4>
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                            📝 {order.special_instructions}
                          </div>
                        </div>
                      )}

                      {/* Order notes */}
                      {order.notes && (
                        <div className="mt-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Notes de commande</h4>
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                            {order.notes}
                          </div>
                        </div>
                      )}

                      {/* Payment info */}
                      <div className="mt-4 bg-white rounded-xl p-4 border border-gray-200">
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>Sous-total</span>
                          <span>{((order.total_amount || 0) - (order.service_fee || 0)).toFixed(2)} €</span>
                        </div>
                        {order.service_fee > 0 && (
                          <div className="flex justify-between text-sm text-gray-500 mt-1">
                            <span>Frais de service</span>
                            <span>{order.service_fee?.toFixed(2)} €</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-gray-900 mt-2 pt-2 border-t">
                          <span>Total</span>
                          <span className="text-emerald-600">{order.total_amount?.toFixed(2)} €</span>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Mode: {order.payment_method === 'Carte' ? 'Paiement en ligne ✓' : 'Paiement au retrait'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Cancel Order Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Annuler la commande
            </DialogTitle>
            <DialogDescription>
              Vous êtes sur le point d'annuler la commande{' '}
              <strong className="text-gray-900">
                {orderToCancel?.order_number}
              </strong>
              . Le client sera notifié et remboursé si le paiement a déjà été effectué.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-sm text-amber-800">
                <strong>Montant à rembourser :</strong>{' '}
                {orderToCancel?.total_amount?.toFixed(2)} €
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Raison de l'annulation *
              </label>
              <Textarea
                placeholder="Ex: Ingrédient indisponible, erreur de commande..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowCancelModal(false)}
              disabled={cancelling}
            >
              Retour
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelOrder}
              disabled={cancelling || !cancelReason.trim()}
            >
              {cancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
